<script setup>
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2ForgotPasswordIllustrationLight from '@images/pages/auth-v2-forgot-password-illustration-light.png'
import authV2ForgotPasswordIllustrationDark from '@images/pages/auth-v2-forgot-password-illustration-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@core/stores/AuthStore'
import { themeConfig } from '@themeConfig'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { passwordValidator } from '@core/utils/validators'
import AppTextField from '@core/components/app-form-elements/AppTextField.vue'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const authThemeImg = useGenerateImageVariant(authV2ForgotPasswordIllustrationLight, authV2ForgotPasswordIllustrationDark)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
const i18n = useI18n()
const router = useRouter()

const resetCode = ref(router.currentRoute.value.query.code)
const password = ref('')
const errorMessage = ref('')
const isFormSubmitting = ref(false)
const isSubmissionSuccessful = ref(false)
const isPasswordVisible = ref(false)

const { recaptchaLoaded, executeRecaptcha } = useReCaptcha()
const authStore = useAuthStore()

async function executeResetPasswordRecaptcha() {
  await recaptchaLoaded()

  return executeRecaptcha('reset_password')
}

async function resetPassword() {
  isFormSubmitting.value = true
  isSubmissionSuccessful.value = false
  errorMessage.value = ''

  try {
    const recaptchaToken = await executeResetPasswordRecaptcha()

    await authStore.resetPassword({
      code: resetCode.value,
      password: password.value,
      recaptchaToken,
    })

    isSubmissionSuccessful.value = true
  } catch (err) {
    console.error(err)

    switch (err.response.data?.error?.code) {
    case 2011:
      errorMessage.value = i18n.t('Password is already used')
      break
    case 2012:
      errorMessage.value = i18n.t('Invalid reset code')
      break
    default:
      errorMessage.value = i18n.t('Something went wrong')
    }
  } finally {
    isFormSubmitting.value = false
  }
}

onBeforeMount(() => {
  if (!resetCode.value) {
    router.replace({ path: '/' })
  }
})
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper bg-surface"
    no-gutters
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 150px;"
        >
          <VImg
            max-width="468"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ $t('Reset password') }} 🔒
          </h4>
          <p class="mb-0">
            {{ $t('Enter your new password in the field below') }}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="resetPassword">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="password"
                  autofocus
                  :rules="[passwordValidator]"
                  :label="$t('Password')"
                  placeholder="············"
                  :disabled="isFormSubmitting || isSubmissionSuccessful"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                  :disabled="isFormSubmitting"
                >
                  {{ $t('Change password') }}
                </VBtn>
              </VCol>

              <VAlert
                v-if="isSubmissionSuccessful || isSubmissionSuccessful"
                :title="$t('Success')"
                :text="$t('Password changed successfully')"
              />

              <ErrorMessage :message="errorMessage" />

              <VCol cols="12">
                <RouterLink
                  class="d-flex align-center justify-center"
                  :to="{ name: 'login' }"
                >
                  <VIcon
                    icon="tabler-chevron-left"
                    size="20"
                    class="me-1 flip-in-rtl"
                  />
                  <span>{{ $t('Back to login') }}</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
